import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Newspaper, ArrowRight, User, Calendar, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const FEATURED_POSTS = [
  {
    id: "1",
    title: "5 Estrategias para vender por WhatsApp este año",
    excerpt: "Aprende cómo cerrar más ventas utilizando la herramienta de mensajería más potente...",
    image: "https://picsum.photos/seed/whatsapp/800/600",
    date: "2024-04-10",
    author: "María García",
    category: "Marketing"
  },
  {
    id: "2",
    title: "Guía completa: Cómo crear tu primer embudo de ventas",
    excerpt: "Los embudos no son solo para expertos. Te mostramos el paso a paso para automatizar tu negocio...",
    image: "https://picsum.photos/seed/funnel/800/600",
    date: "2024-04-12",
    author: "Juan Pérez",
    category: "Automatización"
  },
  {
    id: "3",
    title: "Historias de Éxito: De emprendedor local a ventas nacionales",
    excerpt: "Hablamos con los fundadores de 'Artesanías Andinas' sobre su crecimiento exponencial...",
    image: "https://picsum.photos/seed/success/800/600",
    date: "2024-04-15",
    author: "Elena Rodríguez",
    category: "Casos de Éxito"
  }
];

export function BlogPage() {
  return (
    <div className="flex flex-col gap-12 pb-24">
      {/* Hero Section */}
      <section className="relative bg-slate-900 overflow-hidden py-24">
        <div className="absolute inset-0 bg-primary/10 blur-[120px] -translate-x-1/2 rounded-full" />
        <div className="container px-4 md:px-8 relative z-10 flex flex-col items-center text-center">
          <Badge className="mb-4 py-1 px-4 text-xs font-bold bg-primary hover:bg-primary border-none text-white">
            <Newspaper className="h-3 w-3 mr-2" /> Blog de EmpreMarket
          </Badge>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
            Recursos, Consejos y <span className="text-primary">Estrategias</span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl mb-10">
            Aprende a escalar tu negocio con las mismas herramientas que usan los expertos. 
            Contenido exclusivo para nuestra comunidad de emprendedores.
          </p>
          <div className="relative w-full max-w-lg">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input 
              placeholder="Buscar artículos por tema..." 
              className="pl-12 py-6 rounded-2xl bg-white/10 border-white/20 text-white placeholder:text-slate-500 focus:ring-primary"
            />
          </div>
        </div>
      </section>

      {/* Featured Section */}
      <section className="container px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-black">Últimas Publicaciones</h2>
          <div className="hidden md:flex gap-2">
            <Badge variant="outline" className="cursor-pointer hover:bg-primary/5">Todos</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-primary/5">Marketing</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-primary/5">Automatización</Badge>
            <Badge variant="outline" className="cursor-pointer hover:bg-primary/5">Casos de Éxito</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURED_POSTS.map(post => (
            <Card key={post.id} className="group overflow-hidden rounded-3xl border-slate-100 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full bg-white">
              <div className="aspect-[16/10] overflow-hidden relative">
                <img 
                  src={post.image} 
                  alt={post.title} 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/90 backdrop-blur-sm text-slate-900 border-none hover:bg-white">{post.category}</Badge>
                </div>
              </div>
              <CardContent className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-4 text-xs text-slate-400 mb-4 font-bold uppercase tracking-wider">
                  <div className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(post.date).toLocaleDateString()}</div>
                  <div className="flex items-center gap-1"><User className="h-3 w-3" /> {post.author}</div>
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors leading-tight">
                  {post.title}
                </h3>
                <p className="text-slate-600 text-sm mb-6 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="mt-auto pt-4 border-t border-slate-50 flex items-center justify-between">
                  <Button variant="link" className="p-0 h-auto font-bold text-primary group-hover:gap-2 transition-all">
                    Leer más <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="container px-4 md:px-8">
        <div className="bg-primary/5 rounded-[40px] p-12 md:p-20 text-center relative overflow-hidden border border-primary/10">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2" />
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <h2 className="text-4xl font-black">Únete a nuestra newsletter</h2>
            <p className="text-slate-600 text-lg">
              Recibe semanalmente estrategias probadas para hacer crecer tu emprendimiento. 
              Sin spam, solo valor real.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Input 
                placeholder="Tu correo electrónico" 
                className="py-6 px-6 rounded-2xl bg-white border-slate-200 focus:ring-primary shadow-sm h-auto text-lg"
              />
              <Button size="lg" className="px-10 rounded-2xl h-auto py-6 font-bold text-lg shadow-xl shadow-primary/20">
                Suscribirme Gratis
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
              Únete a +5,000 emprendedores exitosos
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
